/**
 * components/countdown-plate — ЕДИНЫЙ примитив «плашка обратного отсчёта до роллровера
 * доски дня».
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-22): таймер доски дня должен быть ОДНИМ
 * переиспользуемым примитивом, а не верстаться заново на каждом экране. Раньше done-state
 * доски дня собирал его инлайн (`createStatPlate` + локальный `attachCountdown`), а игровой
 * экран — свою плашку с видимой подписью. Owner: «импортируй этот таймер — он уже примитив,
 * просто таймер без слов». Этот модуль — ствол (один источник); экраны его монтируют (ветки).
 *
 * SCOPE: value-only плашка-показатель (`createStatPlate` БЕЗ `label` → только `HH:MM:SS`,
 *   подпись — лишь в `aria-label`) + живой тик 1/сек из `services/countdown` (rollover-
 *   математика + формат НЕ дублируются). Опц. `onRollover` — колбэк при достижении 00:00:00
 *   (экран сам решает побочный эффект: idle ребутит доску, игровой экран авто-финишит
 *   отдельным watcher'ом и колбэк не передаёт).
 * NOT IN SCOPE: размеры/шрифт (от `.btn`-токенов через stat-plate + autofit-группу
 *   `.action-rail .btn`), форматирование (делает services/countdown), сам роллровер-сайд-
 *   эффект (передаётся экраном).
 *
 * Cross-references:
 *   - components/stat-plate.ts (база — value-only плашка).
 *   - services/countdown.ts (startCountdown / rollover-математика / formatHms).
 *   - screens/daily-board-screen.ts (done-state) + screens/board-screen.ts (игровой экран).
 */

import { t } from '../lib/i18n/index.js';
import { startCountdown } from '../services/countdown.js';

import { createStatPlate } from './stat-plate.js';

/**
 * Подпись таймера (единая во всех местах; видна только в aria — слов на плашке нет).
 * Геттер, не const: резолвится по АКТИВНОЙ локали в момент монтажа (locale-реактивность,
 * ленивый t() — как в прошлых i18n-волнах). Reuse существующего ключа board.countdownLabel.
 */
export const countdownPlateAria = (): string => t('game.board.countdownLabel');

export interface CountdownPlateHandle {
  /** Корневой элемент плашки — caller кладёт его в контейнер кнопок (.action-rail). */
  readonly element: HTMLElement;
  /** Остановить живой тик (caller зовёт на teardown/re-render — гигиена интервалов). */
  stop(): void;
}

/**
 * Создать value-only плашку живого обратного отсчёта до следующего роллровера доски дня.
 * Показывает ТОЛЬКО `HH:MM:SS` (без видимых слов); RU-подпись — в `aria-label`. Тик 1/сек
 * через `services/countdown` (на `msRemaining ≤ 0` formatHms даёт 00:00:00, не уходит в минус).
 *
 * @param opts.ariaLabel — подпись для доступности (по умолчанию «Следующая доска через»).
 * @param opts.onRollover — опц. одноразовый колбэк при достижении 00:00:00 (idle-ребут и т.п.).
 */
export function createCountdownPlate(opts?: {
  ariaLabel?: string;
  onRollover?: () => void;
}): CountdownPlateHandle {
  const plate = createStatPlate({
    ariaLabel: opts?.ariaLabel ?? countdownPlateAria(),
    initialValue: '00:00:00',
  });
  let rolledOver = false;
  const handle = startCountdown(
    (hms, msRemaining) => {
      plate.setValue(hms);
      if (msRemaining <= 0 && !rolledOver && opts?.onRollover) {
        rolledOver = true;
        opts.onRollover();
      }
    },
    { intervalMs: 1000 },
  );
  return { element: plate.element, stop: handle.stop };
}
